import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from "@/components/ui/select";

export default function EventEditor({ event, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [towns, setTowns] = useState([]);
  const [cats, setCats] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm({
      title: event.title || "",
      description: event.description || "",
      start_datetime: event.start_datetime ? event.start_datetime.slice(0, 16) : "",
      end_datetime: event.end_datetime ? event.end_datetime.slice(0, 16) : "",
      venue: event.venue || "",
      town: event.town || "",
      category: event.category || "",
      is_paid: !!event.is_paid,
      source_url: event.source_url || "",
      status: event.status || "pending",
    });
    api.get("/admin/towns").then(r => setTowns(r.data));
    api.get("/admin/categories").then(r => setCats(r.data));
  }, [event]);

  const upd = (k) => (v) => setForm({ ...form, [k]: v?.target ? v.target.value : v });

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        ...form,
        start_datetime: new Date(form.start_datetime).toISOString(),
        end_datetime: form.end_datetime ? new Date(form.end_datetime).toISOString() : null,
      };
      await api.patch(`/admin/events/${event.id}`, body);
      toast.success("Saved");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="event-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Edit event</DialogTitle>
          <DialogDescription className="text-slate-500">Edit fields and save to update the event.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2 space-y-1">
            <Label>Title</Label>
            <Input value={form.title} onChange={upd("title")} data-testid="editor-title" />
          </div>
          <div className="col-span-2 space-y-1">
            <Label>Description</Label>
            <Textarea value={form.description} onChange={upd("description")} rows={2} />
          </div>
          <div className="space-y-1">
            <Label>Start</Label>
            <Input type="datetime-local" value={form.start_datetime} onChange={upd("start_datetime")} />
          </div>
          <div className="space-y-1">
            <Label>End</Label>
            <Input type="datetime-local" value={form.end_datetime} onChange={upd("end_datetime")} />
          </div>
          <div className="space-y-1">
            <Label>Venue</Label>
            <Input value={form.venue} onChange={upd("venue")} />
          </div>
          <div className="space-y-1">
            <Label>Town</Label>
            <Select value={form.town || "__none"} onValueChange={(v) => setForm({...form, town: v === "__none" ? "" : v})}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">—</SelectItem>
                {towns.map(t => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Category</Label>
            <Select value={form.category || "__none"} onValueChange={(v) => setForm({...form, category: v === "__none" ? "" : v})}>
              <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">—</SelectItem>
                {cats.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label>Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2 space-y-1">
            <Label>Source URL</Label>
            <Input value={form.source_url} onChange={upd("source_url")} />
          </div>
          <div className="col-span-2 flex items-center gap-2">
            <Switch checked={form.is_paid} onCheckedChange={(v)=>setForm({...form, is_paid: v})} data-testid="editor-paid"/>
            <Label>Paid event (ticketed)</Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#1A3C5A] text-white hover:bg-[#12293e]" data-testid="editor-save">
            {saving ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
